import React, { useEffect, useMemo, useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Drawer, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { TextareaControl } from '@reapit/elements/core/textarea-control'
import { useAdminEnums } from '../../../../api/enums'
import { useAdminMergeFields } from '../../../../api/merge-fields'
import { AdminActionTemplate, EnumOption } from '../../../../api/types'
import { useCreateAdminActionTemplate, useUpdateAdminActionTemplate } from '../../../../api/action-templates'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'
import { buildActivityOptions } from '../event-template-drawer'

interface ActionTemplateValues {
  action: string
  description: string
}

const actionTemplateSchema = yup.object({
  action: yup.string().required('Choose the activity this describes'),
  description: yup.string().trim().required('Enter an audit-log description'),
})

interface ActionTemplateDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  /** The action template being edited, or null to create a new one. */
  actionTemplate: AdminActionTemplate | null
  /** The form's document family ("Agreement" | "Notice") — narrows the Activity choices offered, same as
   * EventTemplateDrawer, since most activities can never fire for a Notice. */
  family?: string
  onSaved: () => void
}

/** Add/Edit drawer for an action template (audit-log description). Creates (POST) or updates (PUT) then refreshes. */
export const ActionTemplateDrawer: React.FC<ActionTemplateDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  actionTemplate,
  family,
  onSaved,
}) => {
  const formId = 'action-template-form'
  const { data: enums } = useAdminEnums()
  const { data: mergeFields } = useAdminMergeFields()
  const [createTemplate, creating] = useCreateAdminActionTemplate(formReferenceID, versionReferenceID)
  const [updateTemplate, updating] = useUpdateAdminActionTemplate(
    formReferenceID,
    versionReferenceID,
    actionTemplate?.referenceID ?? '',
  )
  const isBusy = creating || updating

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ActionTemplateValues>({
    resolver: yupResolver(actionTemplateSchema),
    defaultValues: { action: '', description: '' },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      action: actionTemplate?.action ?? '',
      description: actionTemplate?.description ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, actionTemplate])

  const handleClose = () => onOpenChange(false)

  const actionOptions = useMemo<EnumOption[]>(
    () => buildActivityOptions(enums?.DocumentActivity ?? [], family, actionTemplate?.action),
    [enums, family, actionTemplate],
  )
  const mergeFieldOptions = useMemo<EnumOption[]>(
    () =>
      (mergeFields ?? []).map((mergeField) => ({
        value: mergeField.token,
        label: `${mergeField.category} · ${mergeField.name}`,
      })),
    [mergeFields],
  )

  // Seed descriptions are dense with {{tokens}} (e.g. UserFullName, WithdrawalReason) — the picker below
  // inserts one at the cursor, same mechanism as the recipient field drawer's "Default Value" textarea.
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null)

  const onSubmit: SubmitHandler<ActionTemplateValues> = async (values) => {
    try {
      if (actionTemplate) await updateTemplate(values)
      else await createTemplate(values)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors (incl. the 409 duplicate-action) surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={actionTemplate ? 'Edit Action' : 'Add Action'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Select
              label="Activity *"
              options={actionOptions}
              value={actionOptions.find((option) => option.value === watch('action')) ?? null}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => setValue('action', option?.value ?? '')}
              errorMessage={errors.action?.message}
            />
            <FieldHelp>The document activity this line describes in the document&apos;s activity history.</FieldHelp>
          </div>

          <TextareaControl
            label="Description *"
            helpText="The audit-log line recorded when this activity occurs. Merge tokens (e.g. {{UserFullName}}) are resolved at the time — use the picker below to insert one at the cursor."
            fieldSizing="fixed"
            rows={6}
            {...(() => {
              const { ref: rhfRef, ...rest } = register('description')
              return {
                ...rest,
                ref: (element: HTMLTextAreaElement | null) => {
                  rhfRef(element)
                  descriptionRef.current = element
                },
              }
            })()}
            errorText={errors.description?.message}
          />

          <Select
            label="Insert a merge field"
            isClearable
            optionLabelKey="label"
            optionValueKey="value"
            options={mergeFieldOptions}
            value={null}
            getOptionLabel={(option: EnumOption) => option.label}
            getOptionValue={(option: EnumOption) => option.value}
            onChange={(option: EnumOption | null) => {
              if (!option) return
              const textarea = descriptionRef.current
              const current = watch('description') ?? ''
              const start = textarea?.selectionStart ?? current.length
              const end = textarea?.selectionEnd ?? current.length
              const next = current.slice(0, start) + option.value + current.slice(end)
              setValue('description', next)

              const cursorPosition = start + option.value.length
              requestAnimationFrame(() => {
                textarea?.focus()
                textarea?.setSelectionRange(cursorPosition, cursorPosition)
              })
            }}
          />
        </FieldStack>
      )}
    </Drawer>
  )
}
